import { ApiError, apiRequest, getCurrentAuthToken } from '../api/client'

let pendingChange: Promise<unknown> = Promise.resolve()

function withPushLock<T>(run: () => Promise<T>): Promise<T> {
  if (navigator.locks) return navigator.locks.request('brownie-push-subscription', run)
  const next = pendingChange.then(run, run)
  pendingChange = next.catch(() => undefined)
  return next
}

export function supportsPush(): boolean {
  return window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function isPwa(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true
}

/** Requests the browser permission as the installed PWA opens; a refusal leaves the app usable. */
export async function requestPushPermissionOnPwaEntry(): Promise<void> {
  if (!supportsPush() || !isPwa() || Notification.permission !== 'default') return
  await Notification.requestPermission()
}

export function decodeApplicationKey(value: string): Uint8Array<ArrayBuffer> {
  const raw = atob(value.replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

export async function connectPush(publicKey: string): Promise<void> {
  if (!supportsPush()) throw new Error('Откройте приложение по HTTPS в браузере с поддержкой уведомлений. На iPhone сначала добавьте его на экран «Домой».')
  const token = getCurrentAuthToken()
  assertSession(token)
  const key = decodeApplicationKey(publicKey)
  if (key.length !== 65 || key[0] !== 4) throw new Error('Не удалось включить уведомления. Обновите страницу; если ошибка повторится, обратитесь к администратору.')
  const permission = await Notification.requestPermission()
  assertSession(token)
  if (permission !== 'granted') throw new Error('Разрешите уведомления в настройках сайта, затем включите их снова.')
  await withPushLock(async () => {
    assertSession(token)
    await withTimeout(navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }))
    assertSession(token)
    const registration = await withTimeout(navigator.serviceWorker.ready)
    assertSession(token)
    const existing = await withTimeout(registration.pushManager.getSubscription())
    assertSession(token)
    if (existing && (!existing.options.applicationServerKey || !sameKey(existing.options.applicationServerKey, key))) await replaceSubscription(existing, token)
    assertSession(token)
    let subscription = await withTimeout(registration.pushManager.getSubscription()) ?? await withTimeout(registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }))
    // Neither a delayed permission prompt nor a queued request may change another account's binding.
    assertSession(token)
    try {
      await apiRequest('/notifications/push/subscriptions', { method: 'POST', body: subscription.toJSON(), authToken: token })
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== 'PUSH_SUBSCRIPTION_OWNED') throw error
      assertSession(token)
      await replaceSubscription(subscription, token)
      assertSession(token)
      subscription = await withTimeout(registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }))
      assertSession(token)
      await apiRequest('/notifications/push/subscriptions', { method: 'POST', body: subscription.toJSON(), authToken: token })
    }
    assertSession(token)
  })
}

/** Reconnects a permission that the user already granted, without showing a browser prompt. */
export async function restorePushSubscription(): Promise<void> {
  if (!supportsPush() || Notification.permission !== 'granted' || !getCurrentAuthToken()) return
  const config = await apiRequest<{ enabled: boolean; publicKey: string }>('/notifications/push/config')
  if (config.enabled && config.publicKey) await connectPush(config.publicKey)
}

async function replaceSubscription(subscription: PushSubscription, token: string): Promise<void> {
  if (!await withTimeout(subscription.unsubscribe())) throw new Error('Браузер не обновил подписку. Отключите уведомления, затем включите их снова.')
  try {
    await apiRequest('/notifications/push/subscriptions', { method: 'DELETE', body: { endpoint: subscription.endpoint }, authToken: token, timeout: 5000, notifyOnUnauthorized: false })
  } catch { /* The expired endpoint is also removed after the provider returns 404/410. */ }
}

function assertSession(token: string | null): asserts token is string {
  if (!token || getCurrentAuthToken() !== token) throw new Error('Учётная запись изменилась. Войдите и повторите включение уведомлений.')
}

async function withTimeout<T>(operation: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([operation, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Браузер не подключил уведомления. Проверьте соединение и повторите попытку.')), 20_000)
    })])
  } finally { clearTimeout(timer) }
}

function sameKey(existing: ArrayBuffer, current: Uint8Array): boolean {
  const bytes = new Uint8Array(existing)
  return bytes.length === current.length && bytes.every((byte, index) => byte === current[index])
}

export async function disconnectPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const token = getCurrentAuthToken()
  await withPushLock(async () => {
    if (getCurrentAuthToken() !== token && getCurrentAuthToken() !== null) return
    const registration = await withTimeout(navigator.serviceWorker.getRegistration('/'))
    const subscription = await withTimeout(registration?.pushManager?.getSubscription() ?? Promise.resolve(null))
    if (!subscription) return
    try {
      if (token) await apiRequest('/notifications/push/subscriptions', { method: 'DELETE', body: { endpoint: subscription.endpoint }, authToken: token, timeout: 5000, notifyOnUnauthorized: false })
    } catch { /* Browser unsubscription also expires delivery when the session is offline/expired. */ }
    if (getCurrentAuthToken() === token || getCurrentAuthToken() === null) await withTimeout(subscription.unsubscribe())
  })
}

export async function hasPushSubscription(): Promise<boolean> {
  const token = getCurrentAuthToken()
  if (!supportsPush() || !token || Notification.permission === 'denied') return false
  const registration = await navigator.serviceWorker.getRegistration('/')
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) return false
  assertSession(token)
  const result = await apiRequest<{ subscribed: boolean }>('/notifications/push/subscriptions/status', { method: 'POST', body: { endpoint: subscription.endpoint }, authToken: token })
  assertSession(token)
  return result.subscribed
}
